import { useEffect, useState, useCallback } from "react";
import {
  getShopBySlug,
  listServices,
  listTokensForShop,
  subscribeShop,
  subscribeShopTokens,
  subscribeServices,
} from "./api";
import { withTimeout } from "./diagnostics";

export function useShop(slug) {
  const [shop, setShop] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const refetch = useCallback(async () => {
    if (!slug) {
      setLoading(false);
      return;
    }
    try {
      setError(null);
      const s = await withTimeout(getShopBySlug(slug), 8000, "Loading shop");
      setShop(s);
    } catch (e) {
      setError(e);
    } finally {
      setLoading(false);
    }
  }, [slug]);

  useEffect(() => {
    refetch();
  }, [refetch]);

  useEffect(() => {
    if (!shop?.id) return;
    const unsub = subscribeShop(shop.id, () => refetch());
    // 5-second polling fallback — guarantees shop status (open/closed/blocked)
    // propagates even when Supabase Realtime is throttled or blocked.
    const t = setInterval(refetch, 5000);
    return () => {
      unsub();
      clearInterval(t);
    };
  }, [shop?.id, refetch]);

  return { shop, loading, error, refetch };
}

export function useServices(shopId, { activeOnly = false } = {}) {
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const refetch = useCallback(async () => {
    if (!shopId) {
      setLoading(false);
      return;
    }
    try {
      setError(null);
      const list = await withTimeout(
        listServices(shopId, { activeOnly }),
        8000,
        "Loading services"
      );
      setServices(list);
    } catch (e) {
      setError(e);
    } finally {
      setLoading(false);
    }
  }, [shopId, activeOnly]);

  useEffect(() => {
    refetch();
  }, [refetch]);

  useEffect(() => {
    if (!shopId) return;
    const unsub = subscribeServices(shopId, () => refetch());
    return unsub;
  }, [shopId, refetch]);

  return { services, loading, error, refetch };
}

export function useTokens(shopId) {
  const [tokens, setTokens] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const refetch = useCallback(async () => {
    if (!shopId) {
      setLoading(false);
      return;
    }
    try {
      setError(null);
      const list = await withTimeout(
        listTokensForShop(shopId),
        8000,
        "Loading tokens"
      );
      setTokens(list);
    } catch (e) {
      setError(e);
    } finally {
      setLoading(false);
    }
  }, [shopId]);

  useEffect(() => {
    refetch();
  }, [refetch]);

  useEffect(() => {
    if (!shopId) return;
    const unsub = subscribeShopTokens(shopId, () => refetch());
    const t = setInterval(refetch, 5000);
    return () => {
      unsub();
      clearInterval(t);
    };
  }, [shopId, refetch]);

  return { tokens, loading, error, refetch };
}
